import { useMemo, useState } from 'react';
import { useApp } from '../store';
import { HOW_TO, HELP_GROUPS, howTosFor, type HowTo, type HelpWay } from '../help';
import Icon from './Icon';
import { combo } from '../platform';

/*
 * THE HELP PAGE (#501) — the dedicated, full-screen home of the "how do I…"
 * corpus, approved 14 Oct 2026 (mocks 01 landing / 02 search / 03 lesson answer).
 *
 * PURE COMPOSITION, no new data model: it renders `src/help.ts` (the same corpus
 * the ⌘K palette searches) and reuses the guide's own `drillTo(view, reveal)` →
 * `.just-revealed` to land on the real control and flash it. ⌘K stays the typed
 * quick-jump from anywhere; this is where the Help row now leads, and where an
 * empty search shows the whole index to browse by job.
 *
 * Slice 1 (this): landing + live search + single-target reveal. Slice 2 later:
 * richer multi-step answers. Every answer carries one "Take me there", so help is
 * never a dead end.
 */

// The one worked example the page leads with — the lesson-tagging answer (#500),
// the format every other answer follows: HOW to do it and WHERE the control lives.
// The "Start here" hero is a newcomer's LITERAL first step — starting a board —
// not an edge-case housekeeping task. (It led with filing a lesson, which
// presupposes projects, categories and the Lessons library a day-one user has no
// context for; learnability-witness F-B, 15 Oct 2026. Its group tag is now
// "Getting started", which also settles the hero/group dissonance ux-witness #2
// flagged.) The lesson-filing answer stays in the corpus, found by search.
const FEATURED_ID = 'create-board';
// A few most-asked jobs under Start here, after the featured one. Real corpus ids.
const STARTER_IDS = ['get-signoff', 'raise-change-request', 'find-anything'];
// Recently shipped, flagged "New" so a returning user spots what changed. These
// are the 3.19 board additions; the set is how a new section stays current.
const NEW_IDS = new Set(['view-one-sprint', 'collapse-swimlane', 'add-in-staging', 'in-staging', 'hide-done-column']);

const byId = (id: string): HowTo | undefined => HOW_TO.find((h) => h.id === id);
const entriesIn = (group: string): HowTo[] => HOW_TO.filter((h) => h.group === group);

/** "How do I raise a card?" → "Raise a card" — a compact label for the browse
 *  grid, presentation only (the corpus keeps the full question it is searched by). */
function shortLabel(q: string): string {
  const s = q.replace(/^how do i\s+/i, '').replace(/\?+$/, '').trim();
  return s ? s[0].toUpperCase() + s.slice(1) : q;
}

export default function HelpView() {
  const drillTo = useApp((s) => s.drillTo);
  const [q, setQ] = useState('');
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const searching = q.trim().length > 0;
  const results = useMemo(() => (searching ? howTosFor(q) : []), [q, searching]);
  const go = (w: HelpWay) => {
    if (w.target) drillTo(w.target.view, w.target.reveal);
  };

  // One answer is open at a time. While searching, the best match opens by
  // default so the top of the list is the full answer, not another teaser.
  const activeOpen = expandedId ?? (searching ? results[0]?.id ?? null : null);

  const onSearch = (value: string) => {
    setQ(value);
    setOpenGroup(null);
    setExpandedId(null);
  };
  const openList = (group: string, id: string | null = null) => {
    setOpenGroup(group);
    setExpandedId(id);
  };
  const backToIndex = () => {
    setOpenGroup(null);
    setExpandedId(null);
  };

  const featured = byId(FEATURED_ID);
  const starters = STARTER_IDS.map(byId).filter((h): h is HowTo => !!h);

  const row = (h: HowTo) => {
    const open = activeOpen === h.id;
    return (
      <div className={`help-row ${open ? 'is-open' : ''}`} key={h.id}>
        <button
          className="help-row-head"
          aria-expanded={open}
          onClick={() => setExpandedId((cur) => (cur === h.id ? null : h.id))}
        >
          <span className="help-row-headline">
            <span className="help-row-q">{h.q}</span>
            {NEW_IDS.has(h.id) && <span className="help-new">New</span>}
            <span className="help-row-tag">{h.group}</span>
            <span className="help-row-chev">
              <Icon name="chevron" size={16} />
            </span>
          </span>
          {!open && <span className="help-row-preview">{h.ways[0].steps}</span>}
        </button>
        {open && (
          <div className="help-row-body">
            {h.ways.length === 1 ? (
              <>
                <p className="help-row-answer">{h.ways[0].steps}</p>
                {h.ways[0].target && (
                  <button className="btn btn-primary help-go" onClick={() => go(h.ways[0])}>
                    <Icon name="arrow" size={14} />
                    Take me there
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="help-ways-lead">
                  {h.ways.length === 2 ? 'Two' : h.ways.length === 3 ? 'Three' : h.ways.length} ways — use
                  whichever fits.
                </p>
                {/* Alternatives, not steps — a plain list of selectable routes, each
                    the whole row a click target with one quiet "Take me there". */}
                <ul className="help-ways">
                  {h.ways.map((w, i) => {
                    const main = (
                      <span className="help-way-main">
                        <span className="help-way-via">{w.via}</span>
                        <span className="help-way-steps">{w.steps}</span>
                      </span>
                    );
                    return (
                      <li key={i}>
                        {w.target ? (
                          <button className="help-way" onClick={() => go(w)}>
                            {main}
                            <span className="help-way-go">
                              Take me there
                              <Icon name="arrow" size={14} />
                            </span>
                          </button>
                        ) : (
                          <div className="help-way help-way-static">{main}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="help-page">
      <header className="help-head">
        <h1>Help</h1>
        <p className="help-sub">
          Find how to do anything in FlightDeck. Search, or browse by job — every answer takes you to the
          control and highlights it.
        </p>
      </header>

      <div className="help-search">
        <Icon name="search" size={18} />
        <input
          className="help-search-input"
          placeholder={'Search help — try “how do I sign off?”'}
          value={q}
          onChange={(e) => onSearch(e.target.value)}
          aria-label="Search help"
          autoFocus
        />
        <span className="help-search-kbd" aria-hidden="true">
          {combo('K')}
        </span>
      </div>

      {searching ? (
        <section className="help-results">
          <h2 className="help-h2">
            <b>
              {results.length} {results.length === 1 ? 'answer' : 'answers'}
            </b>{' '}
            for “{q.trim()}”
          </h2>
          {results.length > 0 ? (
            <div className="help-list">{results.map(row)}</div>
          ) : (
            <>
              <p className="help-nomatch-lead">Nothing here matches “{q.trim()}” yet.</p>
              {/* NO DEAD-END (L22, ux-witness #1): a miss still shows the whole
                  browsable index below — the same fallback ⌘K's help mode gives —
                  so the page the person is already on offers a way forward, not
                  only a pointer somewhere else. */}
              <h2 className="help-h2 help-nomatch-all">Browse all help</h2>
              <div className="help-list">{HOW_TO.map(row)}</div>
            </>
          )}
          <p className="help-nomatch">
            Not here? Press <span className="help-kbd-inline">{combo('K')}</span> to search cards, boards and
            screens too.
          </p>
        </section>
      ) : openGroup ? (
        <section className="help-group">
          <button className="help-back" onClick={backToIndex}>
            <Icon name="chevronLeft" size={15} />
            All help
          </button>
          <h2 className="help-h2">
            <b>{openGroup}</b>
            <span className="help-count">{entriesIn(openGroup).length}</span>
          </h2>
          <div className="help-list">{entriesIn(openGroup).map(row)}</div>
        </section>
      ) : (
        <>
          <section className="help-start">
            <h2 className="help-h2">Start here</h2>
            {featured && (
              <div className="help-feature">
                <span className="help-feature-ic">
                  <Icon name="help" size={18} />
                </span>
                <div className="help-feature-main">
                  <div className="help-feature-top">
                    <span className="help-feature-q">{featured.q}</span>
                    <span className="help-row-tag">{featured.group}</span>
                  </div>
                  <p className="help-feature-ans">{featured.ways[0].steps}</p>
                  {featured.ways[0].target && (
                    <button className="help-link" onClick={() => go(featured.ways[0])}>
                      Take me there
                      <Icon name="arrow" size={14} />
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="help-list">{starters.map(row)}</div>
          </section>

          <section className="help-browse">
            <h2 className="help-h2">Browse by job</h2>
            <div className="help-grid">
              {HELP_GROUPS.map((group) => {
                const items = entriesIn(group);
                if (items.length === 0) return null;
                // Surface anything new in the preview, so a returning user spots
                // what changed without opening the group.
                const preview = [
                  ...items.filter((h) => NEW_IDS.has(h.id)),
                  ...items.filter((h) => !NEW_IDS.has(h.id)),
                ].slice(0, 4);
                return (
                  <div className="help-jobcard" key={group}>
                    <div className="help-jobcard-head">
                      <h3>{group}</h3>
                      <span className="help-count">{items.length}</span>
                    </div>
                    <ul className="help-joblist">
                      {preview.map((h) => (
                        <li key={h.id}>
                          <button className="help-jobitem" onClick={() => openList(group, h.id)}>
                            <span>{shortLabel(h.q)}</span>
                            {NEW_IDS.has(h.id) && <span className="help-new">New</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button className="help-seeall" onClick={() => openList(group)}>
                      See all {items.length}
                      <Icon name="arrow" size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
